import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  Close,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';

const severityConfig = {
  error: {
    icon: ErrorOutline,
    color: '#EF4444',
    bgColor: '#FEE2E2',
  },
  warning: {
    icon: WarningAmber,
    color: '#F59E0B',
    bgColor: '#FEF3C7',
  },
  info: {
    icon: InfoOutlined,
    color: '#3B82F6',
    bgColor: '#DBEAFE',
  },
};

export const ConfirmDialog: React.FC = () => {
  const { confirm } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const config = severityConfig[confirm.severity || 'warning'];
  const IconComponent = config.icon;

  return (
    <Dialog
      open={confirm.open}
      onClose={confirm.onCancel}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: isTablet ? '90vw' : '500px',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      {/* Header avec icône */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          pb: 2,
          pt: 3,
          px: 3,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            borderRadius: '50%',
            backgroundColor: config.bgColor,
            flexShrink: 0,
          }}
        >
          <IconComponent
            sx={{
              fontSize: { xs: 28, sm: 32 },
              color: config.color,
            }}
          />
        </Box>
        <Box sx={{ flex: 1, pt: 0.5 }}>
          <Box
            component="h2"
            sx={{
              margin: 0,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {confirm.title}
          </Box>
        </Box>
        {isMobile && (
          <IconButton
            onClick={confirm.onCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      {/* Contenu */}
      <DialogContent sx={{ px: 3, py: 2 }}>
        <DialogContentText
          sx={{
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            ml: { xs: 0, sm: '72px' }, // Align with title on desktop
          }}
        >
          {confirm.message}
        </DialogContentText>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          gap: 1.5,
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={confirm.onCancel}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: 1.25,
            px: 3,
            borderRadius: 2,
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: theme.palette.grey[50],
            },
          }}
        >
          {confirm.cancelText}
        </Button>
        <Button
          onClick={confirm.onConfirm}
          variant="contained"
          fullWidth={isMobile}
          autoFocus
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: 1.25,
            px: 3,
            borderRadius: 2,
            backgroundColor: config.color,
            color: '#fff',
            boxShadow: `0 2px 8px ${config.color}40`,
            '&:hover': {
              backgroundColor: config.color,
              filter: 'brightness(0.9)',
              boxShadow: `0 4px 12px ${config.color}60`,
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
          }}
        >
          {confirm.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
